// Phase 28 — Security actions for the user (B workstream).
//
// Three actions:
//  1. Set password now (admin-chosen)
//  2. Send password-reset email (Phase 26 link, 60 min expiry)
//  3. Toggle email-verified flag
//
// Every action calls a single POST endpoint and writes an admin_audit row
// server-side. All confirmations go through the AlertDialog so we never
// fire a destructive action by mis-click.

import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KeyRound, Mail, ShieldCheck, Loader2, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SecurityTab({ user, userId, onChanged }) {
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(null); // "set_now" | "send_email" | "verify"
  const [confirmOpen, setConfirmOpen] = useState(null); // null | "set_now" | "send_email" | "force_temp"
  const [verifiedToggle, setVerifiedToggle] = useState(Boolean(user?.email_verified));
  const [tempResult, setTempResult] = useState(null);

  const doSetNow = async () => {
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy("set_now");
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, {
        mode: "set_now",
        new_password: pw,
      });
      if (data?.ok) {
        toast.success("Password updated · sessions invalidated");
        setPw("");
        onChanged?.();
      } else {
        toast.error("Password change failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy(null);
      setConfirmOpen(null);
    }
  };

  const doSendEmail = async () => {
    setBusy("send_email");
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, {
        mode: "send_email",
      });
      const ok = data?.status && data.status !== "failed";
      toast[ok ? "success" : "error"](`Reset email · status=${data?.status || "unknown"}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy(null);
      setConfirmOpen(null);
    }
  };

  const doForceTemp = async () => {
    setBusy("force_temp");
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, {});
      if (data?.temporary_password) {
        setTempResult(data.temporary_password);
        toast.success("Temporary password issued");
        onChanged?.();
      } else {
        toast.error("No temporary password returned");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy(null);
      setConfirmOpen(null);
    }
  };

  const doVerifyToggle = async (next) => {
    setBusy("verify");
    setVerifiedToggle(next);
    try {
      await api.post(`/admin/users/${userId}/verify-email`, { verified: next });
      toast.success(next ? "Email marked verified" : "Email un-verified");
      onChanged?.();
    } catch (e) {
      // Revert the toggle UI on failure.
      setVerifiedToggle(!next);
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const copyTemp = () => {
    if (!tempResult) return;
    navigator.clipboard.writeText(tempResult).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="space-y-4 pt-2" data-testid="security-tab">
      {/* SET PASSWORD */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <KeyRound className="h-3 w-3" /> Set password now
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Replace the user's password immediately. Every existing session is invalidated.
        </p>
        <div className="grid gap-2">
          <Label className="text-xs">New password (8+ chars)</Label>
          <div className="flex gap-2">
            <Input
              type={showPw ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Choose a password"
              className="h-9 text-sm"
              data-testid="security-set-password-input"
              autoComplete="new-password"
            />
            <Button size="sm" variant="outline" type="button" onClick={() => setShowPw((s) => !s)} data-testid="security-toggle-show-pw">
              {showPw ? "Hide" : "Show"}
            </Button>
            <Button
              size="sm"
              onClick={() => setConfirmOpen("set_now")}
              disabled={pw.length < 8 || busy === "set_now"}
              data-testid="security-set-password-btn"
            >
              {busy === "set_now" ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : null}
              Set
            </Button>
          </div>
        </div>
      </div>

      {/* SEND RESET EMAIL */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <Mail className="h-3 w-3" /> Email a reset link
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Sends a self-serve reset email (60 min expiry) using the standard Phase 26 template.
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setConfirmOpen("send_email")}
          disabled={busy === "send_email"}
          data-testid="security-send-email-btn"
        >
          {busy === "send_email" ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : <Mail className="me-1 h-3.5 w-3.5" />}
          Send reset email
        </Button>
      </div>

      {/* TEMPORARY PASSWORD */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-800">
          <AlertTriangle className="h-3 w-3" /> Issue temporary password
        </div>
        <p className="mb-3 text-xs text-amber-800">
          Generates a one-time 12-char password and flags <code>must_reset_password</code>. Use only when the user can't receive email.
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setConfirmOpen("force_temp")}
          disabled={busy === "force_temp"}
          data-testid="security-force-temp-btn"
        >
          {busy === "force_temp" ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : null}
          Issue temp password
        </Button>
        {tempResult && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 font-mono text-sm" data-testid="security-temp-result">
            <span className="select-all">{tempResult}</span>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={copyTemp} data-testid="security-temp-copy-btn">
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <span className="ms-auto text-[10px] text-amber-700">Share securely — shown once.</span>
          </div>
        )}
      </div>

      {/* EMAIL VERIFIED */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <ShieldCheck className="h-3 w-3" /> Email verified
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Manually mark this user's email as verified (or un-verified).
        </p>
        <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <span className="text-sm font-medium text-slate-800">
            {verifiedToggle ? "Verified" : "Not verified"}
          </span>
          <Switch
            checked={verifiedToggle}
            onCheckedChange={(v) => doVerifyToggle(v)}
            disabled={busy === "verify"}
            data-testid="security-verify-switch"
          />
        </div>
      </div>

      {/* Confirm dialog for destructive actions */}
      <AlertDialog open={!!confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(null)}>
        <AlertDialogContent data-testid="security-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmOpen === "set_now" && "Set new password?"}
              {confirmOpen === "send_email" && "Send password reset email?"}
              {confirmOpen === "force_temp" && "Issue a temporary password?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmOpen === "set_now" && "This invalidates every active session for this user. They will need to log in again with the new password."}
              {confirmOpen === "send_email" && `An email with a reset link will be sent to ${user?.email}.`}
              {confirmOpen === "force_temp" && "A 12-character temporary password will be generated. The user will be required to change it at next login. Existing sessions are invalidated."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="security-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="security-confirm-accept"
              onClick={() => {
                if (confirmOpen === "set_now") doSetNow();
                else if (confirmOpen === "send_email") doSendEmail();
                else if (confirmOpen === "force_temp") doForceTemp();
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
